/**
 * Turn parsed rows + a MappingConfig into a list of records, each with its form
 * instances and per-cell typed values. Handles the three shapes:
 *   single           — one record per row, one instance.
 *   repeatable_long  — rows grouped by identity key → one record, N instances.
 *   repeatable_wide  — one record per row, one instance per non-empty column block.
 *
 * Transform (number/date/select…) errors are captured per cell so the review
 * grid can surface and let the user fix them before commit.
 */
import { applyTransform } from './transforms'
import type { ColumnMapping, MappingConfig } from './mapping-types'

const GROUP_SEP = ''

export interface BuiltInstance {
  /** field_key → typed value (undefined values are omitted). */
  fieldValues: Record<string, unknown>
  /** field_key → transform error (bad number/date/etc). */
  cellErrors: Record<string, string>
  /** Row index in the original parsed table. */
  sourceRowIndex: number
}

export interface BuiltIdentity {
  mode: MappingConfig['identity']['mode']
  displayId?: string
  matchFieldKey?: string
  matchValue?: string
}

export interface BuiltRecord {
  groupKey: string
  identity: BuiltIdentity
  instances: BuiltInstance[]
}

function buildInstance(
  row: Record<string, string>,
  mappings: ColumnMapping[],
  rowIndex: number,
): BuiltInstance {
  const fieldValues: Record<string, unknown> = {}
  const cellErrors: Record<string, string> = {}
  for (const m of mappings) {
    if (!m.fieldKey) continue
    const { value, error } = applyTransform(row[m.column] ?? '', m.transform)
    if (value !== undefined) fieldValues[m.fieldKey] = value
    if (error) cellErrors[m.fieldKey] = error
  }
  return { fieldValues, cellErrors, sourceRowIndex: rowIndex }
}

function resolveIdentity(row: Record<string, string>, config: MappingConfig): BuiltIdentity {
  const id = config.identity
  if (id.mode === 'match_display_id') {
    return { mode: id.mode, displayId: (row[id.displayIdColumn ?? ''] ?? '').trim() }
  }
  if (id.mode === 'match_field') {
    return {
      mode: id.mode,
      matchFieldKey: id.matchFieldKey,
      matchValue: (row[id.matchColumn ?? ''] ?? '').trim(),
    }
  }
  return { mode: 'create_new' }
}

export function buildRecords(rows: Array<Record<string, string>>, config: MappingConfig): BuiltRecord[] {
  if (config.shape === 'repeatable_wide') {
    return rows.map((row, i) => {
      const instances = config.wideBlocks
        .map((blk) => buildInstance(row, blk.columns, i))
        .filter((inst) => Object.keys(inst.fieldValues).length > 0 || Object.keys(inst.cellErrors).length > 0)
      return { groupKey: String(i), identity: resolveIdentity(row, config), instances }
    })
  }

  const perRow = rows.map((row, i) => ({ row, i, inst: buildInstance(row, config.columns, i) }))

  // Single forms (or long with no grouping key) → one record per row.
  if (config.shape === 'single' || config.identity.groupByColumns.length === 0) {
    return perRow.map(({ row, inst, i }) => ({
      groupKey: String(i),
      identity: resolveIdentity(row, config),
      instances: [inst],
    }))
  }

  // repeatable_long with a grouping key → rows sharing the key share a record.
  const groups = new Map<string, BuiltRecord>()
  const order: string[] = []
  for (const { row, inst } of perRow) {
    const key = config.identity.groupByColumns.map((c) => (row[c] ?? '').trim()).join(GROUP_SEP)
    let rec = groups.get(key)
    if (!rec) {
      rec = { groupKey: key, identity: resolveIdentity(row, config), instances: [] }
      groups.set(key, rec)
      order.push(key)
    }
    rec.instances.push(inst)
  }
  return order.map((k) => groups.get(k)!)
}
